/**
 * Tracks the highest contiguously-received per-bucket `seq` for the active WS
 * session and detects gaps in the live stream.
 *
 * Mirrors the iOS SeqTracker: advances only on actual receipt, never trusts
 * `catchup_complete.latest_seq` to advance, and asks the caller to issue a
 * precise `catchup` frame when a gap appears. Bounded retries protect against
 * permanently missing seqs (server pruned past retention).
 */

export type SeqOutcome =
  | { kind: 'noAction' }
  | { kind: 'requestCatchup'; sinceSeq: number; limit: number };

export const MAX_PENDING_FORWARD = 10_000;
export const MAX_REFILL_RETRIES = 3;
export const MAX_REFILL_LIMIT = 500;
export const REFILL_HEADROOM = 16;

export class SeqTracker {
  private _highestContiguous: number;
  private inflightRefillSince: number | null = null;
  private refillRetries = 0;
  private pendingForward = new Set<number>();
  /** A cold connect (`sinceSeq=0`) receives a server-chosen *tail* — its
   *  first envelope is some high seq, not seq 1. Adopt that first seq as the
   *  baseline instead of diffing against 0; otherwise the jump looks like a
   *  gap and fires a `catchup sinceSeq=0` that pulls the oldest rows. */
  private adoptNextAsBaseline: boolean;

  constructor(initialHighest: number) {
    this._highestContiguous = Math.max(0, initialHighest);
    this.adoptNextAsBaseline = this._highestContiguous === 0;
  }

  get highestContiguous(): number {
    return this._highestContiguous;
  }

  /** Cursor sent on reconnect / used as `since_seq` in catchup requests. */
  get sinceSeqForRefill(): number {
    return this._highestContiguous;
  }

  /** Process one inbound envelope's `seq`. */
  note(seq: number | undefined): SeqOutcome {
    if (seq == null) return { kind: 'noAction' };

    if (this.adoptNextAsBaseline) {
      // First envelope of a cold-connect tail — it defines the cursor.
      this.adoptNextAsBaseline = false;
      this._highestContiguous = seq;
      return { kind: 'noAction' };
    }

    if (seq <= this._highestContiguous) {
      // Replay overlap; safe to ignore for tracking. Caller still dedupes by
      // envelope id at the chat layer.
      this.pendingForward.delete(seq);
      return { kind: 'noAction' };
    }

    if (seq === this._highestContiguous + 1) {
      this._highestContiguous = seq;
      // Slide forward through previously-buffered out-of-order seqs.
      while (this.pendingForward.has(this._highestContiguous + 1)) {
        this.pendingForward.delete(this._highestContiguous + 1);
        this._highestContiguous += 1;
      }
      if (this.inflightRefillSince != null && this._highestContiguous >= this.inflightRefillSince) {
        this.inflightRefillSince = null;
        this.refillRetries = 0;
      }
      return { kind: 'noAction' };
    }

    // seq > highestContiguous + 1 → gap.
    this.pendingForward.add(seq);
    if (this.pendingForward.size > MAX_PENDING_FORWARD) {
      // Defensive: tracker is hopelessly behind. Accept the gap, slide on.
      this.advancePastGap(seq);
      return { kind: 'noAction' };
    }
    return this.scheduleRefillIfNeeded(seq);
  }

  /** Called when a `catchup_complete` arrives. Doesn't advance the cursor. */
  noteCatchupComplete(): void {
    this.inflightRefillSince = null;
    this.refillRetries = 0;
  }

  private scheduleRefillIfNeeded(triggerSeq: number): SeqOutcome {
    // Same gap we're already chasing.
    if (this.inflightRefillSince != null && this.inflightRefillSince === this._highestContiguous) {
      this.refillRetries += 1;
      if (this.refillRetries >= MAX_REFILL_RETRIES) {
        // Accept the gap — server pruned past retention or there's a hole.
        this.advancePastGap(triggerSeq);
        return { kind: 'noAction' };
      }
      return {
        kind: 'requestCatchup',
        sinceSeq: this._highestContiguous,
        limit: this.refillLimit(triggerSeq),
      };
    }
    // New gap: arm an inflight refill.
    this.inflightRefillSince = this._highestContiguous;
    this.refillRetries = 1;
    return {
      kind: 'requestCatchup',
      sinceSeq: this._highestContiguous,
      limit: this.refillLimit(triggerSeq),
    };
  }

  private advancePastGap(triggerSeq: number): void {
    this._highestContiguous = triggerSeq - 1;
    while (this.pendingForward.has(this._highestContiguous + 1)) {
      this.pendingForward.delete(this._highestContiguous + 1);
      this._highestContiguous += 1;
    }
    this.inflightRefillSince = null;
    this.refillRetries = 0;
  }

  private refillLimit(triggerSeq: number): number {
    const raw = (triggerSeq - this._highestContiguous) + REFILL_HEADROOM;
    return Math.min(Math.max(raw, 1), MAX_REFILL_LIMIT);
  }
}
